import axios from 'axios';
import {
    GET_ALL_ACTIONS,
    GET_SECTION_ACCESS,
    ASSIGN_SECTION_ACCESS,
    UNASSIGN_SECTION_ACCESS,
    GET_SECTIONS_IN_FORM,
    REVIEWER_TYPE_USER,
    REVIEWER_TYPE_ROLE,
    ACTION_READ,
    ACTION_WRITE
} from '@/utils/constants/constants.js';

export const section_access_mixin = {
    data: () => ({
        existing_section_access: [],
        selected_section_id: null,
        selected_action_id: null,
        section_list: [],
        actions_list: [],
        new_section_access: {
            section_id: null,
            action_id: null
        },
        edit_workflow_reviewer_id: null,
        edit_workflow_reviewer_access_id: null,
        edit_reviewer_type: null,
        current_reviewer_index: null,
        current_status_index: null,
        sections_with_access: [],
        read_action_id: null,
        write_action_id: null,
        isLoading: false,
        current_reviewer_name: ''
    }),

    methods: {
        async get_all_actions() {
            const response = await axios.get(GET_ALL_ACTIONS, { headers: this.headers });
            const response_status = Reflect.get(Reflect.get(response, 'data'), 'status');

            if (response_status === 'success') {
                this.actions_list = response.data.data.map(action => ({
                    id: action.id,
                    name: action.action_name,
                    description: action.description
                }));

                this.set_read_write_action_ids(response.data.data);
            } else {
                this.show_error('Failed to load actions');
            }
        },

        set_read_write_action_ids(actions) {
            const read = actions.find(a => a.action_name === ACTION_READ);
            const write = actions.find(a => a.action_name === ACTION_WRITE);
            this.read_action_id = read?.id || null;
            this.write_action_id = write?.id || null;
        },

        async open_section_access_modal(status, reviewer_index = 0, reviewer_type = REVIEWER_TYPE_USER) {
            if (!this.validate_inputs(status, reviewer_type)) return;

            await this.ensure_reviewers_loaded(status);

            const reviewers = status.status_reviewers || [];
            if (!this.setup_reviewer_data(reviewers, reviewer_index, reviewer_type)) return;

            await this.finalize_modal_setup(status, reviewer_index);
        },

        validate_inputs(status, reviewer_type) {
            if (!status) {
                this.show_error('Status not provided');
                return false;
            }

            const valid_types = [REVIEWER_TYPE_USER, REVIEWER_TYPE_ROLE];
            if (!valid_types.includes(reviewer_type)) {
                this.show_error('Invalid reviewer type');
                return false;
            }

            return true;
        },

        async ensure_reviewers_loaded(status) {
            if (!status.status_reviewers || status.status_reviewers.length === 0) {
                await this.load_status_reviewers(status);
            }
        },

        setup_reviewer_data(reviewers, reviewer_index, reviewer_type) {
            if (!reviewers.length) {
                this.show_error('No reviewers found for this status');
                return false;
            }

            const reviewer = Reflect.get(reviewers, reviewer_index);
            if (!reviewer?.id) {
                this.show_error('Invalid reviewer data');
                return false;
            }

            this.edit_workflow_reviewer_id = reviewer.id;
            this.edit_workflow_reviewer_access_id = reviewer.id || reviewer.workflow_reviewer_id;
            this.edit_reviewer_type = reviewer_type;
            this.set_reviewer_name(reviewer, reviewer_type);

            return true;
        },

        set_reviewer_name(reviewer, reviewer_type) {
            if (reviewer_type === REVIEWER_TYPE_USER) {
                this.current_reviewer_name = reviewer.user?.first_name + ' ' + reviewer.user?.last_name || 'User';
            } else {
                this.current_reviewer_name = reviewer.role?.role_name || 'Role';
            }
        },

        async finalize_modal_setup(status, reviewer_index) {
            this.current_status_index = status.id;
            this.current_reviewer_index = reviewer_index;
            this.selected_section_id = null;
            this.selected_action_id = null;
            this.isLoading = true;

            if (this.edit_workflow_reviewer_id) {
                await this.get_all_actions().catch(() => null);
                await this.get_sections_for_checkboxes().catch(() => null);
                await this.load_existing_section_access().catch(() => null);
                this.build_sections_with_access();
            }

            this.isLoading = false;
        },

        async load_existing_section_access() {
            if (!this.edit_workflow_reviewer_id || !this.edit_reviewer_type) return;

            const params = {};
            const reviewer_access_id = this.edit_workflow_reviewer_access_id || this.edit_workflow_reviewer_id;
            if (this.edit_reviewer_type === REVIEWER_TYPE_USER) {
                params.workflow_reviewer_user_id = reviewer_access_id;
            } else {
                params.workflow_reviewer_role_id = reviewer_access_id;
            }

            const response = await axios.get(GET_SECTION_ACCESS, {
                params: params,
                headers: this.headers
            }).catch(() => null);

            if (!response) {
                this.show_error('Error loading section access');
                this.existing_section_access = [];
                return;
            }

            if (response.data.status === 'success') {
                this.existing_section_access = [...response.data.data];
            } else {
                this.existing_section_access = [];
            }
        },

        async get_sections_for_checkboxes() {
            if (!this.form_id) {
                this.show_error('Form ID is not available.');
                return;
            }

            const payload = { form_id: this.form_id };
            const response = await axios.post(GET_SECTIONS_IN_FORM, payload, {
                headers: this.headers
            }).catch(() => null);

            if (!response) {
                this.show_error('Error fetching sections');
                this.section_list = [];
                return;
            }

            if (response.data?.data?.length) {
                this.process_sections_response(response.data.data);
            } else {
                this.section_list = [];
            }
        },

        process_sections_response(sections) {
            this.section_list = sections.map(section => {
                const section_id = section.section_id || section.sectionId || section.id || section.ID;
                const form_section_id = section.form_section_id || section.formSectionId || section.form_section || null;
                const section_name = section.section_name || section.name || section.Name;
                const processed_section = {
                    id: section_id,
                    form_section_id: form_section_id,
                    name: section_name,
                    raw_data: section
                };
                return processed_section;
            });
        },

        build_sections_with_access() {
            this.sections_with_access = this.section_list.map(section => {
                const section_obj = {
                    id: section.id,
                    form_section_id: section.form_section_id,
                    name: section.name,
                    has_read: false,
                    has_write: false,
                    read_access_id: null,
                    write_access_id: null
                };

                if (this.existing_section_access && this.existing_section_access.length > 0) {
                    this.process_existing_access_for_section(section, section_obj);
                }

                return section_obj;
            });
        },

        process_existing_access_for_section(section, section_obj) {
            const section_accesses = this.get_matching_section_accesses(section);

            section_accesses.forEach(access => {
                this.process_access_for_section(access, section_obj);
            });
        },

        get_matching_section_accesses(section) {
            const target_form_section_id = section.form_section_id ?? null;
            return this.existing_section_access.filter(access => {
                const access_section_id = this.get_access_section_id(access);
                const access_section_name = this.get_access_section_name(access);
                const name_match = this.check_section_name_match(access_section_name, section.name);

                if (access_section_id !== null && access_section_id !== undefined) {
                    if (target_form_section_id !== null && target_form_section_id !== undefined) {
                        return access_section_id == target_form_section_id;
                    }
                    return access_section_id == section.id;
                }

                return name_match;
            });
        },

        get_access_section_id(access) {
            if (access.form_section?.id) {
                return access.form_section.id;
            }
            if (access.section_id) {
                return access.section_id;
            }
            if (access.form_section && typeof access.form_section === 'number') {
                return access.form_section;
            }
            return null;
        },

        get_access_section_name(access) {
            if (access.section_name) {
                return access.section_name;
            }
            if (access.form_section?.name) {
                return access.form_section.name;
            }
            return '';
        },

        check_section_name_match(access_section_name, section_name) {
            if (!access_section_name || !section_name) {
                return false;
            }
            return access_section_name.toLowerCase() === section_name.toLowerCase();
        },

        process_access_for_section(access, section_obj) {
            if (access.has_access !== true) {
                return;
            }

            this.update_section_access_based_on_action(access, section_obj);
        },

        update_section_access_based_on_action(access, section_obj) {
            if (access.action_name === ACTION_READ) {
                section_obj.has_read = true;
                section_obj.read_access_id = access.id;
            } else if (access.action_name === ACTION_WRITE) {
                section_obj.has_write = true;
                section_obj.write_access_id = access.id;
            }
        },

        async toggle_read_access(section) {
            this.isLoading = true;

            if (section.has_read) {
                await this.handle_remove_read_access(section).catch(() => null);
            } else {
                await this.handle_assign_read_access(section).catch(() => null);
            }

            await this.refresh_section_access_data().catch(() => null);
            this.isLoading = false;
        },

        async handle_remove_read_access(section) {
            if (section.read_access_id) {
                await this.unassign_single_section_access(section.read_access_id);
                this.show_success('Read access unassigned');
                section.has_read = false;
                section.read_access_id = null;
            }
        },

        async handle_assign_read_access(section) {
            if (section.has_write && section.write_access_id) {
                await this.unassign_single_section_access(section.write_access_id);
                section.has_write = false;
                section.write_access_id = null;
            }

            if (this.read_action_id) {
                this.new_section_access.section_id = section.id;
                this.new_section_access.action_id = this.read_action_id;
                await this.save_section_action_access();
                this.show_success('Read access assigned');
            }
        },

        async toggle_write_access(section) {
            this.isLoading = true;

            if (section.has_write) {
                await this.handle_remove_write_access(section).catch(() => null);
            } else {
                await this.handle_assign_write_access(section).catch(() => null);
            }

            await this.refresh_section_access_data().catch(() => null);
            this.isLoading = false;
        },

        async handle_remove_write_access(section) {
            if (section.write_access_id) {
                await this.unassign_single_section_access(section.write_access_id);
                this.show_success('Write access unassigned');
                section.has_write = false;
                section.write_access_id = null;
            }
        },

        async handle_assign_write_access(section) {
            if (section.has_read && section.read_access_id) {
                await this.unassign_single_section_access(section.read_access_id);
                section.has_read = false;
                section.read_access_id = null;
            }

            if (this.write_action_id) {
                this.new_section_access.section_id = section.id;
                this.new_section_access.action_id = this.write_action_id;
                await this.save_section_action_access();
                this.show_success('Write access assigned');
            }
        },

        async refresh_section_access_data() {
            await this.load_existing_section_access();
            this.build_sections_with_access();
        },

        async save_section_action_access() {
            const validation_error = this.validate_section_access_input();
            if (validation_error) {
                this.show_error(validation_error);
                return;
            }

            const payload = this.build_section_access_payload();

            await this.execute_section_access_assignment(payload)
                .then(() => {
                    this.show_success('Section access assigned successfully');
                    this.reset_new_section_access();
                })
                .catch(() => {
                    this.show_error('Error assigning section access');
                });
        },

        validate_section_access_input() {
            if (!this.edit_workflow_reviewer_id || !this.edit_reviewer_type) {
                return 'No reviewer selected for section access assignment';
            }

            if (!this.new_section_access.section_id || !this.new_section_access.action_id) {
                return 'Please select both a section and an action';
            }

            return null;
        },

        build_section_access_payload() {
            const payload = {
                section_actions: {
                    [this.new_section_access.section_id]: [this.new_section_access.action_id]
                }
            };

            if (this.edit_reviewer_type === REVIEWER_TYPE_USER) {
                payload.workflow_reviewer_user_id = this.edit_workflow_reviewer_id;
            } else {
                payload.workflow_reviewer_role_id = this.edit_workflow_reviewer_id;
            }

            return payload;
        },

        async execute_section_access_assignment(payload) {
            const response = await axios.post(ASSIGN_SECTION_ACCESS, payload, {
                headers: this.headers
            });

            const response_status = Reflect.get(Reflect.get(response, 'data'), 'status');
            if (response_status !== 'success') {
                throw new Error('Failed to assign section access');
            }
        },

        reset_new_section_access() {
            this.new_section_access = {
                section_id: null,
                action_id: null
            };
        },

        async unassign_single_section_access(section_access_id) {
            if (!section_access_id) {
                this.show_error('No section access selected for removal');
                return;
            }

            const payload = {
                section_access_id: section_access_id
            };

            const response = await axios.post(UNASSIGN_SECTION_ACCESS, payload, {
                headers: this.headers
            }).catch(() => null);

            if (!response) {
                this.show_error('Error removing section access');
                return;
            }

            const response_status = Reflect.get(Reflect.get(response, 'data'), 'status');
            if (response_status === 'success') {
                this.show_success('Section access removed successfully');
                this.existing_section_access = this.existing_section_access.filter(
                    access => access.id !== section_access_id
                );
            } else {
                this.show_error('Failed to remove section access');
            }
        },

        reset_section_access_modal() {
            this.edit_workflow_reviewer_id = null;
            this.edit_workflow_reviewer_access_id = null;
            this.edit_reviewer_type = null;
            this.current_reviewer_index = null;
            this.current_status_index = null;
            this.existing_section_access = [];
            this.new_section_access = {
                section_id: null,
                action_id: null
            };
            this.sections_with_access = [];
            this.read_action_id = null;
            this.write_action_id = null;
            this.isLoading = false;
            this.current_reviewer_name = '';
        },

        async get_sections() {
            if (!this.form_id) {
                this.show_error('Form ID is not available.');
                return;
            }

            this.section_list = [];
            const payload = { form_id: this.form_id };

            const response = await axios.post(GET_SECTIONS_IN_FORM, payload, {
                headers: this.headers
            }).catch(() => null);

            if (!response) {
                this.show_error('Error fetching sections');
                return;
            }

            if (response.data?.data?.length) {
                this.section_list = response.data.data.map(section => {
                    const processed_section = {
                        ...section,
                        id: section.section_id || section.id,
                        form_section_id: section.form_section_id || section.form_section || null,
                        name: section.section_name || section.name
                    };
                    return processed_section;
                });
            }
        },
    }
};